/**
 * submissionListSort — helper pur (Chunk 9.M.3 / D22).
 *
 * Pourquoi un module séparé
 * --------------------------
 * Avant 9.M.3, `ProjectDetailPage.tsx` rendait la liste des submissions
 * par projet sans tri client-side : ordre serveur (par défaut
 * `created_at desc` côté API). Conséquence : sur un projet avec 5+
 * submissions à différents stades, les soumissions en attente
 * d'extraction (pending/received/extracting/flagged) se mélangent à
 * celles déjà finalisées (ready), forçant l'utilisateur à scanner toute
 * la liste pour trouver "ce qui me reste à faire".
 *
 * 9.M.3 / D22 — on extrait la liste dans `ProjectSubmissionsList.tsx`
 * (read-only, parité visuelle inchangée) et on lui ajoute un tri
 * client-side : statuts actifs/à traiter en haut, puis `created_at desc`.
 *
 * Statuts canoniques
 * -------------------
 * Source de vérité : `app/apps/shared/src/constants.ts:97-105`
 *   SUBMISSION_STATUSES = [
 *     "pending", "received", "extracting", "extracted",
 *     "reviewed", "flagged", "ready"
 *   ]
 *
 * Décision PO 9.M.3 (Q-E) : adapter le helper au runtime réel, ne pas
 * forcer une enum inventée. Les statuts `parsing` / `parsed` qu'on
 * rencontre dans certains namespaces i18n (`fr.json:474-476`) sont des
 * vestiges historiques (drift D58.3) — ils ne remontent pas du backend
 * et n'ont pas de traitement spécial ici. Tout statut inconnu retombe en
 * fin de liste (ordre = +Infinity), jamais de plantage.
 *
 * Ordre canonique 9.M.3 (priorité = "ce qui demande une action utilisateur") :
 *
 *   1. flagged       — anomalies bloquantes ouvertes : action humaine
 *   2. pending       — pas encore reçue (étape avant import)
 *   3. received      — fichier attaché, attend extraction
 *   4. extracting    — extraction en cours (action serveur, bouton bloqué)
 *   5. extracted     — extraction terminée, attend revue humaine
 *   6. reviewed      — revue OK, attend mise en "ready"
 *   7. ready         — finalisée, plus rien à faire
 *
 * Tie-break : `created_at desc` (le plus récent en haut au sein du même
 * groupe). On compare les chaînes ISO directement (lexicographique == temporel
 * pour le format `YYYY-MM-DDTHH:mm:ss.sssZ`).
 *
 * Doctrine 9.M.x : pur Node, zéro import React/DOM/API. Capté par la
 * config Vitest existante (`environment: "node"`,
 * `include: src/** /*.test.ts`).
 */

import type { SubmissionStatus } from "@kollectivia/shared";

import type { SubmissionRow } from "../../../lib/queries/submissions";

/**
 * Rang canonique par statut. Plus petit = plus haut dans la liste.
 *
 * Doublé par `UNKNOWN_RANK` pour les statuts non énumérés (sécurité
 * runtime si l'API expose un nouveau statut avant que le frontend ne soit
 * mis à jour). Ces statuts inconnus tombent en bas de liste, pas au milieu.
 */
const STATUS_RANK: Record<SubmissionStatus, number> = {
  flagged: 0,
  pending: 1,
  received: 2,
  extracting: 3,
  extracted: 4,
  reviewed: 5,
  ready: 6,
};

const UNKNOWN_RANK = Number.POSITIVE_INFINITY;

/**
 * Retourne le rang canonique d'un statut. Statut inconnu → fin de liste.
 *
 * Exporté pour les tests purs et pour permettre à l'UI d'afficher un
 * badge cohérent ("à traiter" si rank < 4, "fait" si rank ≥ 4) sans
 * dupliquer la table.
 */
export function getStatusRank(status: string): number {
  if (status in STATUS_RANK) {
    return STATUS_RANK[status as SubmissionStatus];
  }
  return UNKNOWN_RANK;
}

/**
 * Trie une liste de submissions par ordre canonique 9.M.3 :
 *   1. groupe statut (rank croissant)
 *   2. created_at desc au sein du groupe
 *
 * **Pur** : ne mute pas l'argument (`[...rows].sort(...)`). Sûr à
 * passer directement à React (référence stable si on memoize côté appelant).
 *
 * Cas limites :
 *  - liste vide → liste vide
 *  - statut inconnu → relégué en fin (UNKNOWN_RANK = +Inf)
 *  - created_at manquant/invalide → comparé comme chaîne vide (fin du groupe)
 */
export function sortSubmissionsForList<T extends Pick<SubmissionRow, "status" | "created_at">>(
  rows: readonly T[],
): T[] {
  return [...rows].sort((a, b) => {
    const ra = getStatusRank(a.status);
    const rb = getStatusRank(b.status);
    if (ra !== rb) return ra - rb;
    // Tie-break : created_at desc (ISO 8601 → ordre lexicographique == ordre temporel).
    const ca = a.created_at ?? "";
    const cb = b.created_at ?? "";
    if (ca === cb) return 0;
    return ca < cb ? 1 : -1;
  });
}
